import { Injectable } from '@angular/core';
import { ChatBotStateService } from './chat-bot-state.service';
import { Sections } from './types/deep-linking';
import { WindowService } from './window.service';
import { OneSignalNotificationEventService } from './one-signal-notification-event.service';
import { interval, Observable, EMPTY, of } from 'rxjs';
import { map, filter, take, timeout, catchError, switchMap, scan } from 'rxjs/operators';

@Injectable()
export class DeepLinkingService {
  private originalScrollTo: typeof window.scrollTo;
  private originalScrollBy: typeof window.scrollBy;
  private originalScroll: typeof window.scroll;

  constructor(
    private readonly windowService: WindowService,
    private readonly chatBotStateService: ChatBotStateService,
    private readonly oneSignalNotificationEventService: OneSignalNotificationEventService
  ) {}

  public init() {
    this.handleSection();
    this.oneSignalNotificationEventService.notificationClick$.subscribe((launchUrl) => {
      this.handleDeepLink(launchUrl);
    });
  }

  private mockScrollFunctions() {
    this.originalScrollBy = window.scrollBy;
    this.originalScroll = window.scroll;
    this.originalScrollTo = window.scrollTo;

    window.scrollBy = () => {};
    window.scroll = () => {};
    window.scrollTo = () => {};
  }

  private restoreScrollFunctions() {
    window.scrollBy = this.originalScrollBy;
    window.scroll = this.originalScroll;
    window.scrollTo = this.originalScrollTo;
  }

  private scrollToSection(section: string) {
    if (section === Sections.CHAT_BOT) {
      this.chatBotStateService.openChatBotModal();
      return;
    }

    this.findAnchor(section)
      .pipe(switchMap((anchor) => this.waitForStablePosition(anchor)))
      .subscribe((scrollPosition) => {
        this.scrollToPosition(scrollPosition);
      });
  }

  /**
   * Selects the anchor until it is found or the timeout is reached.
   * @param section - The section to find the anchor for.
   * @returns An observable that emits the anchor element.
   */
  private findAnchor(section: string): Observable<Element> {
    const anchor = document.querySelector(`[data-section="${section}"]`);

    if (anchor) return of(anchor);

    return interval(500).pipe(
      map(() => document.querySelector(`[data-section="${section}"]`)),
      filter((anchor) => anchor !== null),
      take(1),
      timeout(2500),
      catchError(() => {
        console.warn(`Deep link target not found: ${section}`);
        this.restoreScrollFunctions();
        return EMPTY;
      })
    );
  }

  /**
   * Waits for the position to be stable and then scrolls to the position.
   * @param anchor - The anchor element to wait for.
   * @returns An observable that emits the position.
   */
  private waitForStablePosition(anchor: Element): Observable<number> {
    const offset = 100;

    return interval(1000).pipe(
      map(() => anchor.getBoundingClientRect().top + window.scrollY - offset),
      scan(
        (acc, currentTop) => {
          if (acc.previousTop !== null && currentTop === acc.previousTop) {
            return {
              previousTop: currentTop,
              stableCount: acc.stableCount + 1,
              top: currentTop,
            };
          }

          return {
            previousTop: currentTop,
            stableCount: 0,
            top: currentTop,
          };
        },
        { previousTop: null as number | null, stableCount: 0, top: 0 }
      ),
      filter((state) => state.stableCount >= 3),
      take(1),
      map((state) => state.top)
    );
  }

  private scrollToPosition(top: number) {
    this.originalScrollTo.call(window, {
      top,
      behavior: 'smooth',
    });

    setTimeout(() => {
      this.restoreScrollFunctions();
    }, 5000);
  }

  public handleDeepLink(launchUrl: string) {
    const url = new URL(launchUrl);
    const section = url.searchParams.get('section');

    if (!this.isSameUrl(launchUrl)) {
      this.windowService.redirectToHref(launchUrl);
      return;
    }

    if (!section) return;

    this.mockScrollFunctions();
    this.scrollToSection(section);
  }

  private handleSection() {
    const url = new URL(this.windowService.getHref());
    const section = url.searchParams.get('section');

    if (!section) return;

    this.mockScrollFunctions();
    this.scrollToSection(section);
  }

  private isSameUrl(launchUrl: string) {
    const urlWithoutQueries = this.windowService.getHref().split('?')[0].replace(/\/$/, '');
    const launchUrlWithoutQueries = launchUrl.split('?')[0].replace(/\/$/, '');
    return urlWithoutQueries === launchUrlWithoutQueries;
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/deep-linking.service.ts